/**
 * The demo's guided tour, its photo/AI lock and its view cap (owner, 2026-10-12, demo option (a)). DEMO ONLY: a real business never
 * sees any of it (components/DemoTour.tsx renders only for the sample café, and every wrapper below asks `config.demo` first).
 *
 * THE TOUR. At the start the visitor picks who they are: "Owner or manager" or "Employee" (screens/SignIn.tsx). Each role gets 8 real
 * screens of the sample café, in order, each with a one-line caption, Back and Next, and "3 of 8". Every screen and the end offer the
 * same two choices (B = MAT: the ask rides on what they just saw, one tap; an open door, never a wall):
 *   - "See what your employees see" (owner tour) / "See what your owner sees" (employee tour): the other tour, from its first screen;
 *   - "Use it free for 25 days": sign in → their own kitchen's setup ("Is this you?" in Salt Lake) → the card step (the existing
 *     Checkout, 25 days, card required; core guided.ts "trial").
 * Why these screens (docs/ux/STORIES-OPERATORS.md P1, the coffee-shop owner who also works the line; docs/ux/STORIES-STAFF.md E4–E11,
 * the barista mid-rush): each stop is a moment the persona has every day, in the order the day runs, one thing per screen, and the
 * plain words pass the passability bar (docs/EASY.md: short sentences, named things, no "tasks" or "actions").
 *
 * THE LOCK. Photos and AI never run in the demo: food photos, Snap → done, the certificate scan, place photos, the AI room check,
 * Ask's step-by-step AI answers, voice translation, a paper log photo. Each shows the same two choices instead (`announceLock`).
 *
 * THE CAP. DEMO_VIEW_CAP content views per session (default 8; the server's DEMO_VIEW_CAP as a meta tag, else this build's
 * VITE_DEMO_VIEW_CAP). Content: a guide, a log, the binder, records, an Ask answer. Not content: menus, tabs (the same screen with
 * another tab), and Back. The next content view past the cap shows the same two choices instead. Each cap hit is counted with an
 * anonymous event (core anonymousEvent: a path and a control name, no id, no cookie).
 *
 * THE ASK CAP (owner, 2026-10-13: "the demo allows you to use too much without making a plan for yourself... specifically for asking
 * questions"). DEMO_ASK_CAP (2) questions a session, counted in this browser (sessionStorage, with the tour). Answers come only from
 * the catalog (core askCatalog: exact calculators, "What do I do if..." moments, pinned answers): the demo's mock never calls a model
 * (mock/api.ts `ask`), so there is no AI request to count. After the 2nd answer, and on any 3rd try, Ask's box is replaced by a
 * sign-up card (components/DemoCta.tsx AskSignup) and `ask` refuses with DemoAskCapError.
 */
import { useSyncExternalStore } from "react";
import { ASK_FREE_QUESTIONS } from "@logwave/core";
import { anonymousEvent } from "@logwave/core/src/compliance/measure.ts";
import { ApiError, type Api } from "../api.ts";
import { config } from "../env.ts";
import { demoSignupUrl } from "./demoAsk.ts";
import { ACCOUNT_METHODS, DemoCtaError, announceCta } from "./demoCta.ts";

export type TourRole = "owner" | "employee";

export interface TourStop {
  /** Where the screen is in the sample café (a real route of the app). */
  path: string;
  /** The one line under "3 of 8". */
  caption: string;
  /** Why this screen, for this persona (kept with the stop so a change has to answer it). */
  why: string;
}

/** The owner's day: what the inspector found, the day's list, a check done with help, the 10-second fridge check, the proof, the team, labels, a question. */
export const OWNER_TOUR: readonly TourStop[] = [
  { path: "/inspection", caption: "Is this you? Your last inspection, item by item, and how you keep each one fixed.",
    why: "P1's first question is 'what did the inspector write about us?' The public record is the hook, and 'Is this you?' is how their own setup starts." },
  { path: "/today", caption: "Today: what is done, what is next, and who did it.",
    why: "P1 works the line. One list answers 'are we OK right now?' without asking anyone." },
  { path: "/do?log=sanitizer&guided=1", caption: "The sanitizer test, one step at a time, with the right number shown.",
    why: "The person doing it may be new. Help at each step is how the owner trusts it was done right when they weren't there." },
  { path: "/do?log=cold_units", caption: "Fridge temperature: tap the number, then Save. One screen.",
    why: "The fridge reading is the check done most often (Food Code 3-501.16). It has to take seconds mid-rush." },
  { path: "/prove", caption: "Your binder: every record, ready for the inspector.",
    why: "The day the inspector comes, P1 hands over the proof instead of hunting for paper." },
  { path: "/team", caption: "Your team: who checked in today and whose food handler card runs out soon.",
    why: "Owners carry the team in their head. This shows who is OK to work and what is due, at a glance." },
  { path: "/food/label", caption: "Food labels: what it is, when it was made, and when to throw it out.",
    why: "Date marking (Food Code 3-501.17) is a common write-up. A label in two taps prevents it." },
  { path: "/today?ask=1", caption: "Ask: type a food safety question and get the answer with its source.",
    why: "The owner is the one everyone asks. Ask answers in the moment, with where the rule comes from." },
];

/** The employee's shift: check in, the shift's list, how to do it, log it, label food, report a problem, ask, end the shift. */
export const EMPLOYEE_TOUR: readonly TourStop[] = [
  { path: "/check-in", caption: "Start of shift: say you're OK to work. Your answers stay private.",
    why: "E4: the first thing on every shift. Only 'OK to work' is shared, never the answers." },
  { path: "/today", caption: "Your list for this shift: tap one to do it.",
    why: "E5/E6: the barista wants to know what is theirs and finish it. One list, in order." },
  { path: "/learn/how/cold_units", caption: "How to take a fridge temperature, step by step.",
    why: "E6/E10: a new person learns the check the first time they do it, not in a binder." },
  { path: "/do?log=cold_units", caption: "Log the fridge: tap the number, then Save.",
    why: "E6: the reading takes seconds, and too warm tells them what to do right there." },
  { path: "/food/label", caption: "Label food: what it is, and the day to throw it out.",
    why: "E11: labels are made all shift long, with wet hands. Two taps." },
  { path: "/report", caption: "Something wrong? Report it: a pest, a spill, a broken fridge, someone sick.",
    why: "E8: the person in charge hears about it on their phone. No one has to remember to tell them." },
  { path: "/learn?ask=1", caption: "Ask: 'How long can milk sit out?' The answer, with its source.",
    why: "E9: questions come up on the line. Ask answers without finding a manager." },
  { path: "/team/day", caption: "End of shift: what got done today, and what the next person picks up.",
    why: "The handoff: the closer sees what is done and what is left, and the next shift starts clean." },
];

export const TOURS: Record<TourRole, readonly TourStop[]> = { owner: OWNER_TOUR, employee: EMPLOYEE_TOUR };
/** Who the sample signs in as for each tour (screens/SignIn.tsx; mock/demo.ts DEMO_PEOPLE). */
export const TOUR_PERSON: Record<TourRole, { email: string; name: string }> = {
  owner: { email: "owner@example.com", name: "Olive Owner" },
  employee: { email: "sam@example.com", name: "Sam Ortiz" },
};

/** The words: one place. */
export const TOUR_WORDS = {
  pickTitle: "Who are you?",
  pickOwner: "Owner or manager",
  pickEmployee: "Employee",
  pickNote: "A sample coffee shop. Nothing you do leaves this browser.",
  of: (n: number, total: number) => `${n} of ${total}`,
  back: "Back",
  next: "Next",
  finish: "Finish",
  switchTo: (role: TourRole) => (role === "owner" ? "See what your employees see" : "See what your owner sees"),
  free: "Use it free for 25 days",
  endTitle: "That's the tour.",
  endBody: "Your kitchen gets your own fridges, your own team and your own inspection.",
  again: "Start the tour again",
  lockTitle: "This works in your own kitchen",
  lockPhoto: "Photos are read for real in your own kitchen. Here in the sample, they are switched off.",
  lockAi: "Step-by-step AI answers are made for real in your own kitchen. Here in the sample, they are switched off.",
  capTitle: "You've seen the sample",
  capBody: "Set up your own kitchen to keep going. Your own fridges, your own team, your own records.",
  goBack: "Go back",
} as const;

export const DEMO_VIEW_CAP_DEFAULT = 8;

/** The view cap: the server's (a meta tag on the demo page), else this build's VITE_DEMO_VIEW_CAP, else 8. Whole numbers 1–1000. */
export function demoViewCap(doc: Document | undefined = typeof document === "undefined" ? undefined : document, buildValue: string | undefined = buildCap()): number {
  const ok = (v: string | null | undefined) => { const t = (v ?? "").trim(); if (!/^\d{1,4}$/.test(t)) return null; const n = Number(t); return n >= 1 && n <= 1000 ? n : null; };
  return ok(doc?.querySelector('meta[name="cc-demo-view-cap"]')?.getAttribute("content")) ?? ok(buildValue) ?? DEMO_VIEW_CAP_DEFAULT;
}
function buildCap(): string | undefined {
  try { return ((import.meta as unknown as { env?: Record<string, string | undefined> }).env ?? {}).VITE_DEMO_VIEW_CAP; } catch { return undefined; }
}

/* ─────────────── this session's tour and views (sessionStorage: "per session"; memory where it's blocked) ─────────────── */

interface TourState { role: TourRole | null; step: number; /** Navigate to the step once the app is ready (after a sign-in switch). */ go?: true; done?: true; views: number; /** Sample questions asked this session. */ asks?: number; /** What they typed (for the sign-up, if it can carry them). */ asked?: string[] }
const KEY = "cc.demo.tour";
const EMPTY: TourState = { role: null, step: 0, views: 0 };
let mem: TourState | null = null;
const listeners = new Set<() => void>();
const store = (): Storage | undefined => { try { return typeof sessionStorage === "undefined" ? undefined : sessionStorage; } catch { return undefined; } };

function read(): TourState {
  if (mem) return mem;
  try {
    const v = JSON.parse(store()?.getItem(KEY) ?? "null") as TourState | null;
    mem = v && typeof v.step === "number" && typeof v.views === "number" ? v : EMPTY;
  } catch { mem = EMPTY; }
  return mem;
}
function write(next: TourState) {
  mem = next;
  try { store()?.setItem(KEY, JSON.stringify(next)); } catch { /* this page load only */ }
  for (const l of listeners) l();
}
const subscribe = (fn: () => void) => { listeners.add(fn); return () => { listeners.delete(fn); }; };

export const tourState = (): TourState => read();
export function useTour(): TourState { return useSyncExternalStore(subscribe, read, () => EMPTY); }
export const tourActive = (s: TourState = read()) => s.role !== null && !s.done;

/** Start (or switch to) a role's tour at its first screen. `go`: navigate there when the app is ready. */
export function startTour(role: TourRole) { const p = read(); write({ ...p, role, step: 0, go: true, done: undefined }); }
export function setTourStep(step: number) { const p = read(); if (!p.role) return; write({ ...p, step: Math.max(0, Math.min(TOURS[p.role].length - 1, step)), done: undefined }); }
export function finishTour() { const p = read(); write({ ...p, done: true, go: undefined }); }
export function clearTourGo() { const p = read(); if (p.go) write({ ...p, go: undefined }); }
/** Start over (tests). */
export function resetTour() { mem = null; try { store()?.removeItem(KEY); } catch { /* ignore */ } lock = null; for (const l of listeners) l(); for (const l of lockListeners) l(); }

/* ─────────────── content views ─────────────── */

/** The content screens: a guide, a log, the binder, records. Menus (Today, Learn, Team, More…) are not. */
const CONTENT: readonly RegExp[] = [
  /^\/learn\/(how|library|lesson|surfaces|stains|spills)\/[^/]+$/, /^\/learn\/playbook$/, /^\/rooms\/guide\/[^/]+$/, /^\/objects\/guide$/, /^\/kits\/[^/]+$/,
  /^\/prove$/, /^\/audit-trail$/, /^\/activity(\/[^/]+)?$/, /^\/team\/logs$/, /^\/inspection$/, /^\/people\/(?!add$|permissions$)[^/]+$/,
];
/** The view a location is, for counting: null for a menu or anything else that isn't content. A tab (other query or hash) on the same screen is the same view. */
export function contentView(pathname: string, search = ""): string | null {
  const p = pathname.replace(/\/+$/, "") || "/";
  if (p === "/do") {
    const q = new URLSearchParams(search);
    const k = q.get("check") ?? q.get("log");
    return k ? `/do?${q.has("check") ? "check" : "log"}=${k}` : null;
  }
  return CONTENT.some((re) => re.test(p)) ? p : null;
}

/** One more content view. "ok" under the cap; "capped" for the one past it (the card shows instead, and the hit is counted). */
export function takeView(where: string, cap = demoViewCap()): "ok" | "capped" {
  const p = read();
  if (p.views >= cap) { countCapHit(where); return "capped"; }
  write({ ...p, views: p.views + 1 });
  return "ok";
}
export const demoViews = () => read().views;

/* ─────────────── the Ask cap ─────────────── */

export const DEMO_ASK_CAP = ASK_FREE_QUESTIONS;
export const demoAsks = () => read().asks ?? 0;
export const askCapped = (cap = DEMO_ASK_CAP) => demoAsks() >= cap;
/** One more sample question, kept as typed (500 characters at most, like Ask). "capped" for the one past the cap: nothing is counted. */
export function takeAsk(question: string, cap = DEMO_ASK_CAP): "ok" | "capped" {
  const p = read();
  if ((p.asks ?? 0) >= cap) return "capped";
  write({ ...p, asks: (p.asks ?? 0) + 1, asked: [...(p.asked ?? []), question.trim().slice(0, 500)] });
  return "ok";
}
/** Whether Ask's box is replaced by the sign-up card now (the demo build only). */
export function useAskCapped(): boolean {
  const t = useTour();
  return config.demo && (t.asks ?? 0) >= DEMO_ASK_CAP;
}
/** The refusal Ask answers with past the cap (the card is already showing in the sheet). */
export class DemoAskCapError extends ApiError {
  constructor() { super(403, "demo_ask_cap", "You have asked your 2 sample questions."); this.name = "DemoAskCapError"; }
}

/** The screen the visitor is on, as an app route (the demo's /demo/ base taken off), for the anonymous count. */
const appPath = () => { try { return location.pathname.replace(/^\/demo(?=\/|$)/, "") || "/"; } catch { return "/"; } };

/** The anonymous count of a cap hit (core anonymousEvent: no id, no cookie). Same origin as the demo page; never breaks the screen. */
export function countCapHit(where: string, send: (body: string) => void = postAnon) {
  const ev = anonymousEvent({ surface: "web", kind: "tap", path: where.split("?")[0] || "/", control: "demo-view-cap", width: typeof window === "undefined" ? 1024 : window.innerWidth });
  if (ev) send(JSON.stringify({ events: [ev] }));
}
function postAnon(body: string) {
  try { void fetch("/measure/anon", { method: "POST", headers: { "content-type": "application/json" }, body, keepalive: true, credentials: "omit" }).catch(() => undefined); } catch { /* counting never breaks a screen */ }
}

/* ─────────────── the two choices (photo/AI lock, the cap) ─────────────── */

export type LockKind = "photo" | "ai" | "cap";
let lock: LockKind | null = null;
const lockListeners = new Set<() => void>();
const lockSubscribe = (fn: () => void) => { lockListeners.add(fn); return () => { lockListeners.delete(fn); }; };
export function announceLock(kind: LockKind) { lock = kind; for (const l of lockListeners) l(); }
export function closeLock() { lock = null; for (const l of lockListeners) l(); }
export function useLock(): LockKind | null { return useSyncExternalStore(lockSubscribe, () => lock, () => null); }

/** "Use it free for 25 days": sign in, their own kitchen's setup (Utah: "Is this you?"), then the card step (core guided.ts "trial"). */
export const freeTrialUrl = (host?: string, src = "demo-tour") => demoSignupUrl("gate", host, { state: "UT", go: "trial", src });

/** The refusal a locked call answers with (the card is already showing). */
export class DemoLockedError extends ApiError {
  constructor(public kind: LockKind) { super(403, "demo_locked", kind === "cap" ? TOUR_WORDS.capBody : kind === "ai" ? TOUR_WORDS.lockAi : TOUR_WORDS.lockPhoto); this.name = "DemoLockedError"; }
}

/** The api methods that send a photo or run AI. In the demo each shows the two choices instead of running. */
export const PHOTO_METHODS: readonly string[] = [
  "foodPhoto", "addFoodPhoto", "foodSnap", "foodScan", "saveCertificate", "putPlacePhoto", "setupScan", "spotCheckPhotos", "menuScanPhotos",
  "aiRoomCheckSweep", "aiRoomCheckPhoto", "uploadScan",
];
export const AI_METHODS: readonly string[] = ["voiceTranslate"];

/** The demo's api: photo and AI calls show the two choices; an Ask answer is a content view; an Ask with no guide answer (where a real
 * kitchen gets a step-by-step AI answer) shows the AI lock. Only when this is the demo build (config.demo); otherwise the api as is. */
export function withDemoLock(api: Api): Api {
  const out: Record<string, unknown> = { ...api };
  const stop = (name: string, kind: LockKind) => {
    const fn = (api as unknown as Record<string, unknown>)[name];
    if (typeof fn !== "function") return;
    out[name] = (...a: unknown[]) => {
      if (!config.demo) return (fn as (...x: unknown[]) => unknown).apply(api, a);
      announceLock(kind);
      return Promise.reject(new DemoLockedError(kind));
    };
  };
  for (const m of PHOTO_METHODS) stop(m, "photo");
  for (const m of AI_METHODS) stop(m, "ai");
  // An action that needs a real account opens its card instead (lib/demoCta.ts ACCOUNT_METHODS).
  for (const [name, id] of Object.entries(ACCOUNT_METHODS)) {
    const fn = (api as unknown as Record<string, unknown>)[name];
    if (typeof fn !== "function") continue;
    out[name] = (...a: unknown[]) => {
      if (!config.demo) return (fn as (...x: unknown[]) => unknown).apply(api, a);
      announceCta(id);
      return Promise.reject(new DemoCtaError(id));
    };
  }
  out.ask = async (orgId: string, body: Parameters<Api["ask"]>[1]) => {
    if (!config.demo) return api.ask(orgId, body);
    // The 3rd question is never answered (the sheet shows the sign-up card). The view cap is checked before a question is counted.
    if (askCapped()) throw new DemoAskCapError();
    if (takeView(appPath()) === "capped") { announceLock("cap"); throw new DemoLockedError("cap"); }
    takeAsk(body.question ?? "");
    const r = await api.ask(orgId, body);
    if (!r.grounded && (body.question ?? "").trim().length >= 3) announceLock("ai");
    return r;
  };
  return out as unknown as Api;
}

/** Demo only: a tap that would open a file picker or the camera shows the photo lock instead. Returns the undo. */
export function guardDemoPhotos(root: Document = document): () => void {
  const onClick = (e: Event) => {
    const t = e.target as Element | null;
    const input = t && typeof t.closest === "function" ? (t.closest("input[type=file]") as HTMLInputElement | null) : null;
    if (!input) return;
    e.preventDefault(); e.stopPropagation();
    announceLock("photo");
  };
  root.addEventListener("click", onClick, true);
  const md = typeof navigator !== "undefined" ? navigator.mediaDevices : undefined;
  const real = md?.getUserMedia;
  if (md && real) md.getUserMedia = () => { announceLock("photo"); return Promise.reject(new DOMException(TOUR_WORDS.lockPhoto, "NotAllowedError")); };
  return () => { root.removeEventListener("click", onClick, true); if (md && real) md.getUserMedia = real; };
}
